import React, { useState, useEffect, useCallback } from 'react';
import { AppNav } from '../../components/layout/AppNav';
import { Badge } from '../../components/ui/Badge';
import { adminService, type AdminOffer, type AdminOfferDetail, type AdminOffersQuery, type AdminReseller } from '../../services/admin.service';
import { formatSAR, formatDate } from '../../utils/formatters';
import { useToast } from '../../components/ui/Toast';

const STATUSES = ['', 'Draft', 'In Progress', 'Sent', 'Accepted', 'Rejected', 'Closed'];
const QUARTERS = [1, 2, 3, 4];

export const AdminOrdersPage: React.FC = () => {
  const { showToast } = useToast();
  const [offers, setOffers] = useState<AdminOffer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [resellers, setResellers] = useState<AdminReseller[]>([]);

  const [filters, setFilters] = useState<AdminOffersQuery>({ limit: 50 });
  const [detailModal, setDetailModal] = useState<AdminOfferDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [closeModal, setCloseModal] = useState<{ offerId: string; offerNumber: string } | null>(null);
  const [closeReason, setCloseReason] = useState<'Success' | 'Rejected' | 'Lost' | ''>('');
  const [closeNote, setCloseNote] = useState('');
  const [closing, setClosing] = useState(false);

  const load = useCallback((p: number, q: AdminOffersQuery) => {
    setLoading(true);
    adminService.getOffers({ ...q, page: p })
      .then((res) => { setOffers(res.data); setTotal(res.total); })
      .catch(() => showToast('Failed to load orders', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  useEffect(() => {
    adminService.getResellers().then(setResellers).catch(() => undefined);
    load(1, { limit: 50 });
  }, [load]);

  const applyFilter = (next: AdminOffersQuery) => {
    setFilters(next);
    setPage(1);
    load(1, next);
  };

  const setQuarter = (q: number) => {
    const year = new Date().getFullYear();
    applyFilter({ ...filters, quarter: q, year, from: undefined, to: undefined });
  };

  const clearFilters = () => applyFilter({ limit: 50 });

  const openDetail = (id: string) => {
    setDetailLoading(true);
    adminService.getOfferDetail(id)
      .then(setDetailModal)
      .catch(() => showToast('Failed to load offer details', 'error'))
      .finally(() => setDetailLoading(false));
  };

  const handleClose = () => {
    if (!closeModal || !closeReason) return;
    setClosing(true);
    adminService.closeOffer(closeModal.offerId, closeReason, closeNote || undefined)
      .then(() => {
        showToast('Offer closed', 'success');
        setCloseModal(null);
        setCloseReason('');
        setCloseNote('');
        load(page, filters);
      })
      .catch(() => showToast('Failed to close offer', 'error'))
      .finally(() => setClosing(false));
  };

  const totalPages = Math.ceil(total / (filters.limit ?? 50));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <AppNav title="Orders" subtitle={`${total} total offers across all resellers`} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

        {/* Filters */}
        <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
            <FilterField label="Reseller">
              <select value={filters.resellerId ?? ''} onChange={(e) => applyFilter({ ...filters, resellerId: e.target.value || undefined })} style={selectStyle}>
                <option value="">All resellers</option>
                {resellers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </FilterField>

            <FilterField label="Status">
              <select value={filters.status ?? ''} onChange={(e) => applyFilter({ ...filters, status: e.target.value || undefined })} style={selectStyle}>
                {STATUSES.map((s) => <option key={s} value={s}>{s || 'All statuses'}</option>)}
              </select>
            </FilterField>

            <FilterField label="From">
              <input type="date" value={filters.from ?? ''} onChange={(e) => applyFilter({ ...filters, from: e.target.value || undefined, quarter: undefined, year: undefined })} style={inputStyle} />
            </FilterField>

            <FilterField label="To">
              <input type="date" value={filters.to ?? ''} onChange={(e) => applyFilter({ ...filters, to: e.target.value || undefined, quarter: undefined, year: undefined })} style={inputStyle} />
            </FilterField>

            <div>
              <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Quarter</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {QUARTERS.map((q) => (
                  <button key={q} onClick={() => setQuarter(q)} style={{
                    padding: '6px 10px', border: '1px solid #e9ecef', borderRadius: '6px',
                    background: filters.quarter === q ? '#0f172a' : '#fff',
                    color: filters.quarter === q ? '#fff' : '#374151',
                    cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit',
                  }}>Q{q}</button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => adminService.exportOffers(filters)} style={btnSecondaryStyle}>Export CSV</button>
              <button onClick={clearFilters} style={btnSecondaryStyle}>Clear</button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e9ecef', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>Loading…</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                  <Th>Offer #</Th>
                  <Th>Reseller</Th>
                  <Th>Agent</Th>
                  <Th>Customer</Th>
                  <Th>Status</Th>
                  <Th>Total</Th>
                  <Th>Created</Th>
                  <Th>{' '}</Th>
                </tr>
              </thead>
              <tbody>
                {offers.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => openDetail(o.id)}
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                  >
                    <Td><span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{o.offerNumber}</span></Td>
                    <Td>{o.resellerName}</Td>
                    <Td>{o.agentName}</Td>
                    <Td>{o.customer?.nameEn ?? '—'}</Td>
                    <Td><Badge label={o.status} /></Td>
                    <Td>{formatSAR(o.total)}</Td>
                    <Td>{formatDate(o.createdAt)}</Td>
                    <Td>
                      {o.status === 'Accepted' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setCloseModal({ offerId: o.id, offerNumber: o.offerNumber }); setCloseReason(''); setCloseNote(''); }}
                          style={{ padding: '4px 10px', background: '#0d9488', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit' }}
                        >
                          Close
                        </button>
                      )}
                    </Td>
                  </tr>
                ))}
                {offers.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>No offers found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
            <button disabled={page === 1} onClick={() => { setPage(page - 1); load(page - 1, filters); }} style={btnSecondaryStyle}>Prev</button>
            <span style={{ lineHeight: '34px', fontSize: '13px', color: '#6c757d' }}>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => { setPage(page + 1); load(page + 1, filters); }} style={btnSecondaryStyle}>Next</button>
          </div>
        )}
      </div>

      {/* Detail loading overlay */}
      {detailLoading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px 32px', fontSize: '14px', color: '#374151' }}>Loading offer details…</div>
        </div>
      )}

      {/* Detail modal */}
      {detailModal && (
        <Modal title={`Offer ${detailModal.offerNumber}`} onClose={() => setDetailModal(null)} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            {/* Customer */}
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Customer</div>
              {detailModal.customer ? (
                <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.7' }}>
                  <div><strong>{detailModal.customer.nameEn ?? '—'}</strong></div>
                  {detailModal.customer.crNumber && <div>CR: {detailModal.customer.crNumber}</div>}
                  {detailModal.customer.phone && <div>Phone: {detailModal.customer.phone}</div>}
                  {detailModal.customer.email && <div>Email: {detailModal.customer.email}</div>}
                </div>
              ) : <div style={{ fontSize: '13px', color: '#9ca3af' }}>No customer attached</div>}
            </div>
            {/* Offer info */}
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Details</div>
              <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.7' }}>
                <div>Reseller: {detailModal.resellerName}</div>
                <div>Agent: {detailModal.agentName}</div>
                <div>Status: <Badge label={detailModal.status} /></div>
                <div>Created: {formatDate(detailModal.createdAt)}</div>
              </div>
            </div>
          </div>

          {/* Authorized person */}
          {detailModal.authorizedPerson && (
            <div style={{ marginBottom: '16px', padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', fontSize: '13px', color: '#374151' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: '12px' }}>Authorized Person</span>
              {detailModal.authorizedPerson.nameEn}
              {detailModal.authorizedPerson.mobile && ` · ${detailModal.authorizedPerson.mobile}`}
            </div>
          )}

          {/* Items */}
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Products</div>
          <div style={{ border: '1px solid #e9ecef', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={thStyle}>Product</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Location</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Unit Price</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {detailModal.items?.map((item) => (
                  <tr key={item.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={tdStyle}>{item.product?.name ?? '—'}</td>
                    <td style={tdStyle}><span style={{ fontSize: '11px', background: '#f1f5f9', borderRadius: '4px', padding: '2px 6px' }}>{item.product?.category ?? '—'}</span></td>
                    <td style={tdStyle}>{item.location?.nameEn ?? '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{item.quantity}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatSAR(item.unitPrice)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatSAR(item.total)}</td>
                  </tr>
                ))}
                {(!detailModal.items || detailModal.items.length === 0) && (
                  <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No items</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <div style={{ fontSize: '13px', color: '#374151', textAlign: 'right', lineHeight: '1.8' }}>
              <div>Subtotal: {formatSAR(detailModal.subtotal)}</div>
              <div>VAT (15%): {formatSAR(detailModal.vatAmount)}</div>
              <div style={{ fontWeight: 700, fontSize: '15px', marginTop: '4px' }}>Total: {formatSAR(detailModal.total)}</div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button onClick={() => setDetailModal(null)} style={btnSecondaryStyle}>Close View</button>
            {detailModal.status === 'Accepted' && (
              <button
                onClick={() => {
                  setDetailModal(null);
                  setCloseModal({ offerId: detailModal.id, offerNumber: detailModal.offerNumber });
                  setCloseNote('');
                }}
                style={{ ...btnPrimaryStyle, background: '#0d9488' }}
              >
                Mark as Closed
              </button>
            )}
          </div>
        </Modal>
      )}

      {/* Close modal */}
      {closeModal && (
        <Modal title={`Close offer ${closeModal.offerNumber}`} onClose={() => setCloseModal(null)}>
          <p style={{ fontSize: '14px', color: '#374151', marginBottom: '16px' }}>
            Select a closure reason before marking this offer as <strong>Closed</strong>.
          </p>
          <label style={labelStyle}>Closure Reason <span style={{ color: '#ef4444' }}>*</span></label>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {(['Success', 'Rejected', 'Lost'] as const).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setCloseReason(r)}
                style={{
                  flex: 1, padding: '8px 4px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                  fontFamily: 'inherit', cursor: 'pointer', transition: 'all .12s',
                  border: closeReason === r ? 'none' : '1.5px solid #e5e7eb',
                  background: closeReason === r
                    ? r === 'Success' ? '#0d9488' : r === 'Rejected' ? '#ef4444' : '#f59e0b'
                    : '#f9fafb',
                  color: closeReason === r ? '#fff' : '#374151',
                }}
              >
                {r}
              </button>
            ))}
          </div>
          <label style={labelStyle}>Note (optional)</label>
          <textarea
            value={closeNote}
            onChange={(e) => setCloseNote(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Additional context..."
            style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
            <button onClick={() => setCloseModal(null)} style={btnSecondaryStyle}>Cancel</button>
            <button onClick={handleClose} disabled={closing || !closeReason} style={{ ...btnPrimaryStyle, opacity: !closeReason ? 0.5 : 1, cursor: !closeReason ? 'not-allowed' : 'pointer' }}>
              {closing ? 'Closing…' : 'Confirm Close'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const FilterField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div><div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>{label}</div>{children}</div>
);

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }> = ({ title, onClose, children, wide }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
    <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: wide ? '760px' : '480px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6c757d' }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{children}</th>
);
const Td: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <td style={{ padding: '12px 16px', color: '#374151' }}>{children}</td>
);

const thStyle: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle: React.CSSProperties = { padding: '10px 12px', color: '#374151', verticalAlign: 'middle' };
const selectStyle: React.CSSProperties = { padding: '7px 10px', border: '1px solid #e9ecef', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', background: '#fff' };
const inputStyle: React.CSSProperties = { padding: '7px 10px', border: '1px solid #e9ecef', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '6px' };
const btnPrimaryStyle: React.CSSProperties = { padding: '8px 18px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' };
const btnSecondaryStyle: React.CSSProperties = { padding: '7px 14px', background: '#fff', color: '#374151', border: '1px solid #e9ecef', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' };
